import React, { useEffect, useMemo, useState } from 'react';
import { getExogena1001, getExogena1003, getExogena1005, getExogena1006, API_BASE } from '../services/api';

const fmtCOP = n => Number(n || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FORMATOS = [
  { id: '1001', label: '1001 — Pagos a terceros',     fetch: getExogena1001, cols: ['cpt','concepto','nit','razon_social','total'] },
  { id: '1003', label: '1003 — Retenciones practicadas', fetch: getExogena1003, cols: ['nit','razon_social','ciudad','retefuente','reteiva','reteica','total'] },
  { id: '1005', label: '1005 — IVA descontable',      fetch: getExogena1005, cols: ['nit','razon_social','ciudad','num_comprobantes','total'] },
  { id: '1006', label: '1006 — IVA generado',         fetch: getExogena1006, cols: ['nit','razon_social','ciudad','num_comprobantes','total'] },
];

const HEADER = {
  nit: 'NIT', razon_social: 'Razón social', ciudad: 'Ciudad',
  num_comprobantes: 'Comprobantes', cpt: 'Concepto', concepto: 'Descripción',
  retefuente: 'Retefuente', reteiva: 'ReteIVA', reteica: 'ReteICA',
  total: 'Total',
};

function exportarCSV(data, formato) {
  const cols = FORMATOS.find(f => f.id === formato).cols;
  const header = cols.map(c => HEADER[c]).join(',');
  const rows = data.registros.map(r =>
    cols.map(c => {
      const v = r[c];
      if (v == null) return '';
      if (typeof v === 'string' && v.includes(',')) return `"${v.replace(/"/g, '""')}"`;
      return v;
    }).join(',')
  );
  const csv = [header, ...rows].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `exogena_${formato}_${data.ano}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function InformacionExogena() {
  const [tab, setTab] = useState('1001');
  const [ano, setAno] = useState(new Date().getFullYear() - 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const cargar = async () => {
    setLoading(true); setError('');
    try {
      const fn = FORMATOS.find(f => f.id === tab).fetch;
      const { data } = await fn(ano);
      setData(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar');
    } finally { setLoading(false); }
  };
  useEffect(() => { cargar(); /* eslint-disable-line */ }, [tab, ano]);

  const formato = useMemo(() => FORMATOS.find(f => f.id === tab), [tab]);

  const descargar = async (tipo) => {
    try {
      const resp = await fetch(`${API_BASE}/api/contabilidad/exogena/${tab}/${tipo}?ano=${ano}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('erp_token')}`, ...(localStorage.getItem('erp_acting_tenant') ? { 'x-acting-tenant': localStorage.getItem('erp_acting_tenant') } : {}) },
      });
      if (!resp.ok) throw new Error();
      const url = URL.createObjectURL(await resp.blob());
      const a = document.createElement('a'); a.href = url; a.download = `exogena_${tab}_${ano}.${tipo === 'xml' ? 'xml' : 'xlsx'}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { alert('No se pudo descargar'); }
  };

  return (
    <div style={S.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <p style={S.sub}>
          Reportes anuales DIAN derivados de los comprobantes contabilizados.
          Sirve para preparar la información oficial — el contador valida y la presenta.
        </p>
        <span style={S.hint}>{data && `${data.total_terceros} terceros · Total ${fmtCOP(data.total_general)}`}</span>
      </div>

      <div style={{ ...S.tabs, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {FORMATOS.map(f => (
            <button key={f.id}
                    style={tab === f.id ? S.tabActive : S.tab}
                    onClick={() => setTab(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <label style={S.lbl}>Año</label>
          <input type="number" min="2020" max="2099" value={ano}
                 onChange={e => setAno(Number(e.target.value))}
                 style={S.input} />
          {data && data.registros.length > 0 && (
            <>
              <button onClick={() => exportarCSV(data, tab)} style={S.btnSec}>↓ CSV</button>
              <button onClick={() => descargar('excel')} style={S.btnSec}>↓ Excel</button>
              <button onClick={() => descargar('xml')} style={{ ...S.btnSec, borderColor: '#16a34a', color: '#86efac' }}>↓ XML DIAN</button>
            </>
          )}
        </div>
      </div>

      {error && <div style={S.error}>{error}</div>}
      {loading && <div style={S.loading}>Cargando…</div>}
      {data && !loading && (
        <>
          <div style={S.tituloFmt}>{data.titulo}</div>
          <table style={S.table}>
            <thead>
              <tr>
                {formato.cols.map(c => (
                  <th key={c} style={{ ...S.th, textAlign: ['retefuente','reteiva','reteica','total','num_comprobantes'].includes(c) ? 'right' : 'left' }}>
                    {HEADER[c]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.registros.length === 0
                ? <tr><td colSpan={formato.cols.length} style={{ ...S.td, textAlign: 'center', padding: 30, color: '#94a3b8' }}>Sin movimientos para el formato y año</td></tr>
                : data.registros.map((r, i) => (
                  <tr key={i}>
                    {formato.cols.map(c => (
                      <td key={c} style={{
                        ...S.td,
                        textAlign: ['retefuente','reteiva','reteica','total','num_comprobantes'].includes(c) ? 'right' : 'left',
                        fontFamily: ['nit','retefuente','reteiva','reteica','total','num_comprobantes'].includes(c) ? 'monospace' : 'inherit',
                      }}>
                        {['total','retefuente','reteiva','reteica'].includes(c) ? fmtCOP(r[c]) : (r[c] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              {data.registros.length > 0 && (
                <tr style={{ background: '#f1f5f9', fontWeight: 600 }}>
                  <td style={S.td} colSpan={formato.cols.length - 1}>Total</td>
                  <td style={{ ...S.td, textAlign: 'right', fontFamily: 'monospace' }}>{fmtCOP(data.total_general)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

const S = {
  container: { padding: 24, maxWidth: 1300, margin: '0 auto' },
  header:    { marginBottom: 16 },
  title:     { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  sub:       { color: '#64748b', margin: '4px 0 0', fontSize: 13, maxWidth: 700 },
  toolbar:   { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' },
  lbl:       { fontSize: 12, color: '#64748b', fontWeight: 500 },
  input:     { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff', width: 100 },
  hint:      { color: '#64748b', fontSize: 13 },
  btnSec:    { marginLeft: 'auto', padding: '6px 12px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  tabs:      { display: 'flex', gap: 4, borderBottom: '1px solid #334155', marginBottom: 14, flexWrap: 'wrap', paddingBottom: 4 },
  tab:       { padding: '8px 14px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, color: '#94a3b8', borderBottom: '2px solid transparent', fontWeight: 500 },
  tabActive: { padding: '8px 14px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, color: '#f1f5f9', borderBottom: '2px solid #3b82f6', fontWeight: 600 },
  tituloFmt: { background: '#0f172a', color: '#fff', padding: '8px 16px', borderRadius: '6px 6px 0 0', fontSize: 14, fontWeight: 600 },
  error:     { background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 },
  loading:   { padding: 40, textAlign: 'center', color: '#64748b' },
  table:     { width: '100%', borderCollapse: 'collapse', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '0 0 6px 6px', overflow: 'hidden' },
  th:        { background: '#f8fafc', padding: '8px 12px', fontSize: 11, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid #e2e8f0' },
  td:        { padding: '4px 12px', fontSize: 13, color: '#1e293b', borderBottom: '1px solid #f1f5f9' },
};
